/**
 * contract-columns.tsx — wspólna konfiguracja KOLUMN listy umów.
 *
 * Jedno źródło prawdy dla dwóch ekranów, które pokazują umowy jako tabelę z kolumnami „jak na
 * liście": strony `Contracts` oraz sekcji kokpitu decyzji (`pages/processes/decision-sections`,
 * „Grupuj według: Kartoteka"). Zmiana tutaj jest widoczna na obu — kolumny nie mogą się rozjechać.
 *
 * Katalog core `contracts` to WYŁĄCZNIE 14 pól WSPÓLNYCH 7 klas dokumentów
 * (`DOCUMENT_COMMON_CORE_FIELDS`, `seed-system-modules.ts`). Pola SPECYFICZNE umowy (numer u klienta,
 * status umowy, okres, wartość, strona główna) żyją w satelicie `document_contract_details` i NIE
 * mają odpowiednika w katalogu (`system_module_fields`) — zostają statycznymi kolumnami poza
 * `buildCoreColumns`/rejestrem nadpisań (`buildContractDetailColumns`).
 *
 * Zawartość: rejestr nadpisań renderu kolumn rdzennych (`CONTRACT_OVERRIDES`), slugi obsługiwane
 * osobno (`CONTRACTS_EXCLUDE_SLUGS`), remap sortu (`CONTRACTS_SORT_SLUG_OVERRIDES`), kolumny satelity
 * umowy (`buildContractDetailColumns`), slugi dołączane do `?columns=` (`CONTRACT_ALWAYS_SLUGS`,
 * `CONTRACT_COLUMN_COMPANIONS`), mapa id kolumny → slug backendu (`CONTRACT_COLUMN_ID_TO_SLUG`),
 * prefiks kluczy i18n kolumn procesu (`CONTRACT_PROCESS_LABEL_PREFIX`) i typ wiersza listy
 * (`ContractRow`). Kolumny rdzenne/dodatkowe buduje katalog pól (`buildCoreColumns`/
 * `buildExtColumns` z `ext-list-columns`); kolumny procesu — wspólne `process-columns.tsx`.
 */

import { CONTRACT_DOCUMENT_STATUSES, CONTRACT_STATUSES, DOCUMENT_URGENCIES } from '@saas/shared';

import type { ColumnDefinition } from '@/components/configurable-table';
import {
  contractDocumentStatusLabel,
  contractStatusLabel,
  documentUrgencyLabel,
} from '@/lib/documents/document-select-labels';
import { PROCESS_COLUMN_ID_TO_SLUG } from '@/lib/documents/process-columns';
import {
  buildStaticSelectOverride,
  codeFilterOptions,
  type ColumnCompanionRule,
  type CoreColumnOverride,
} from '@/lib/ext-list-columns';

/** Prefiks kluczy i18n kolumn procesu umów (patrz `buildProcessColumns`). */
export const CONTRACT_PROCESS_LABEL_PREFIX = 'tenant.documents.contracts';

/**
 * Slugi rdzenne obsługiwane OSOBNO (dedykowane buildery), więc pomijane przez `buildCoreColumns`
 * — inaczej powstałaby zdublowana kolumna. Firma → `buildCompanyColumn`; opiekun/źródło →
 * wspólny `buildDocumentCoreRefColumns` (`withSource: true` — umowy MAJĄ kolumnę źródła na
 * liście, w odróżnieniu od faktur, które renderują ją osobno przez `SourceBadge`).
 */
export const CONTRACTS_EXCLUDE_SLUGS: ReadonlySet<string> = new Set([
  'company_id',
  'owner_user_id',
  'source',
]);

/**
 * Nadpisania slug→slug-sortu dla `?sort=` (reszta: id kolumny = slug, fallback w
 * `useServerListControls`). Dwie kolumny satelity umowy (`document_contract_details`) mają `id`
 * (zachowany dla zgodności zapisanych układów) inny niż slug backendu, po którym faktycznie ma
 * sens sort: „Okres" (`id: 'period'`) sortuje po dacie początku (`period_from`), „Strona główna"
 * (`id: 'counterparty'`) po buforowanej NAZWIE (`counterparty_name_cached`), nie po id wiersza.
 */
export const CONTRACTS_SORT_SLUG_OVERRIDES: Readonly<Record<string, string>> = {
  period: 'period_from',
  counterparty: 'counterparty_name_cached',
};

/**
 * Slugi dołączane do `?columns=` ZAWSZE, niezależnie od widocznych kolumn: `gcrecord` — czyta go
 * akcja wiersza (restore/delete), nie jest kolumną.
 */
export const CONTRACT_ALWAYS_SLUGS: readonly string[] = ['gcrecord'];

/**
 * Reguły towarzysza dla `?columns=`: kolumny satelity umowy czytają dodatkowe pola (numer umowy —
 * flagę braku numeru, okres — datę końca i flagę bezterminowości, wartość — flagę poufności,
 * strona główna — NIP).
 */
export const CONTRACT_COLUMN_COMPANIONS: readonly ColumnCompanionRule[] = [
  { whenSlug: 'contract_number', addSlug: 'contract_number_missing' },
  { whenSlug: 'period_from', addSlug: 'period_to' },
  { whenSlug: 'period_from', addSlug: 'period_indefinite' },
  { whenSlug: 'contract_value', addSlug: 'value_confidential' },
  { whenSlug: 'counterparty_name_cached', addSlug: 'counterparty_tax_id_cached' },
];

/**
 * Mapa id kolumny → slug backendu dla `?columns=` — część STAŁA: dedykowane buildery (firma ma id
 * `company`, kategoria, opiekun, źródło) i kolumny statyczne (satelita umowy, bez odpowiednika w
 * katalogu) mapowane jawnie oraz kolumny procesu. Kolumny z katalogu (`buildCoreColumns`)
 * dopisuje wołający pętlą `id = slug`.
 */
export const CONTRACT_COLUMN_ID_TO_SLUG: Readonly<Record<string, string>> = {
  company: 'company_id',
  category: 'category',
  owner_user_id: 'owner_user_id',
  source: 'source',
  contract_number: 'contract_number',
  contract_status: 'contract_status',
  period: 'period_from',
  contract_value: 'contract_value',
  counterparty: 'counterparty_name_cached',
  ...PROCESS_COLUMN_ID_TO_SLUG,
};

/**
 * Rejestr nadpisań kolumn rdzennych umów (per-kartoteka) — TYLKO dla pól obecnych w katalogu
 * (`isCore`), których generyk (typ→komórka) nie wystarcza. Klucze = slugi z
 * `DOCUMENT_COMMON_CORE_FIELDS` (`seed-system-modules.ts`) — pola specyficzne umowy (satelita,
 * patrz `buildContractDetailColumns`) NIE mają wpisu tutaj, bo nie mają odpowiadającego
 * `field` w katalogu (`buildCoreColumns` woła override wyłącznie po slugu pola z `fields`).
 * Sortowalność bierze z `ctx.resolveSortable?.(ctx.field)` (ta sama serwerowa reguła co generyk).
 */
export const CONTRACT_OVERRIDES: Readonly<Record<string, CoreColumnOverride<ContractRow>>> = {
  system_number: ({ field, accessor, resolveSortable }) => ({
    id: field.slug,
    accessor,
    header: field.name,
    defaultVisible: true,
    defaultWidth: 160,
    sortable: resolveSortable?.(field) ?? false,
    headerFilter: { category: 'text', fieldSlug: 'system_number' },
    renderCell: (value) => (
      <span className="font-mono text-xs text-text-secondary">{value ? String(value) : '—'}</span>
    ),
  }),
  title: ({ field, accessor, resolveSortable }) => ({
    id: field.slug,
    accessor,
    header: field.name,
    defaultVisible: true,
    defaultWidth: 240,
    hideable: false,
    sortable: resolveSortable?.(field) ?? false,
    headerFilter: { category: 'text', fieldSlug: 'title' },
    renderCell: (value) => (
      <span className="font-medium text-text-primary">{value ? String(value) : '—'}</span>
    ),
  }),
  // Status DOKUMENTU (`documents.status`: draft/received/approved/rejected — enum formularza umowy,
  // nie słownik) — odróżnić od „Status umowy" (`contract_status`, satelita
  // `document_contract_details`) w `buildContractDetailColumns`. Domyślnie SKRYTA (jak przed
  // migracją) — pole rzadko używane, „Status umowy" jest tym, co user faktycznie widzi.
  // Customowy override całkowicie ZASTĘPUJE kolumnę generyczną (nie łata jej), więc filtr i komórkę
  // wystawia `buildStaticSelectOverride`: lista wyboru w wierszu filtrów (backend whitelistuje pole —
  // `CONTRACT_CORE_COLUMN_WHITELIST`) i komórka z jednej funkcji, także po ręcznym odkryciu kolumny
  // w Kreatorze Listy. Nazwy statusów (nie surowe kody) — te same na karcie umowy, w komórce i w filtrze
  // (decyzja 2026-10-28; zastępuje notatkę z 2026-10-09 „zapisane słowa, polskie nazwy osobną zmianą”).
  status: buildStaticSelectOverride<ContractRow>({
    codes: CONTRACT_DOCUMENT_STATUSES,
    labelOf: contractDocumentStatusLabel,
    defaultVisible: false,
    defaultWidth: 120,
    // Wygaszony tekst komórki — wygląd sprzed zmiany (generyczna komórka bez klas dawała tu kolor bazowy).
    cellClassName: 'text-text-secondary',
  }),
  // Pilność — ten sam zbiór kodów i te same nazwy co w pozostałych klasach dokumentów (i na karcie umowy).
  urgency: buildStaticSelectOverride<ContractRow>({
    codes: DOCUMENT_URGENCIES,
    labelOf: documentUrgencyLabel,
  }),
};

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${m[3]}.${m[2]}.${m[1]}`;
}

function formatPeriod(from: string | null, to: string | null, indefinite: boolean): string {
  if (indefinite) return `${formatDate(from)} — ∞`;
  if (from && to) return `${formatDate(from)} – ${formatDate(to)}`;
  if (from) return `od ${formatDate(from)}`;
  if (to) return `do ${formatDate(to)}`;
  return '—';
}

function formatAmount(amount: string | null | undefined): string {
  if (amount == null) return '—';
  const n = Number(amount);
  if (Number.isNaN(n)) return amount;
  return new Intl.NumberFormat('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

/**
 * Pola specyficzne umowy (satelita `document_contract_details`) — BEZ odpowiednika w katalogu
 * (`system_module_fields`), więc statyczne kolumny poza `buildCoreColumns`/rejestrem nadpisań (nie
 * mają `field` do dopasowania po slugu): numer umowy, status umowy, okres, wartość, strona główna.
 *
 * `isSortable` — serwerowa sortowalność kolumny systemowej (`meta.sortable` ostatniej odpowiedzi;
 * id kolumn `period`/`counterparty` remapuje na slug sortu `CONTRACTS_SORT_SLUG_OVERRIDES`).
 * Kolumny są typowane wierszem listy umów (`ContractRow`) — tym samym wierszem budują je strona
 * listy i sekcja kokpitu decyzji (rzut na wspólny typ sekcji robi dopiero rejestr zestawów).
 *
 * `isFilterable` — serwerowa filtrowalność po SLUGU BACKENDU (`meta.filterable` ostatniej odpowiedzi,
 * BEZ remapu — inaczej niż sort: okres filtruje się po `period_from`, strona główna po
 * `counterparty_name_cached`). Gdy `true`, kolumna dostaje filtr w wierszu filtrów: numer i strona
 * główna — tekst („zawiera"), „Stan umowy” — lista wyboru wartości karty umowy, okres — data początku,
 * wartość — liczba. Pominięte/`false`: komórka filtra pusta (`filterable: false`) — kolumna bez filtra nie ma
 * martwego pola „Szukaj…" (wpisana wartość nie miałaby slugu, więc nigdzie by nie poleciała).
 */
export function buildContractDetailColumns(args: {
  t: (key: string) => string;
  isSortable: (slug: string) => boolean;
  isFilterable?: (slug: string) => boolean;
}): ColumnDefinition<ContractRow>[] {
  const { t, isSortable, isFilterable } = args;
  /**
   * Filtr kolumny po slugu backendu albo pusta komórka, gdy serwer go nie serwuje. `extra` — reszta
   * konfiguracji filtra (np. `options` listy wyboru).
   */
  const filterOn = (
    category: 'text' | 'number' | 'date' | 'select',
    filterSlug: string,
    extra: Omit<
      NonNullable<ColumnDefinition<ContractRow>['headerFilter']>,
      'category' | 'fieldSlug'
    > = {},
  ): Pick<ColumnDefinition<ContractRow>, 'filterable' | 'headerFilter'> => ({
    filterable: false,
    ...(isFilterable?.(filterSlug)
      ? { headerFilter: { category, fieldSlug: filterSlug, ...extra } }
      : {}),
  });
  return [
    {
      id: 'contract_number',
      accessor: 'contractNumber',
      header: t('tenant.documents.contracts.columns.contractNumber'),
      defaultVisible: true,
      defaultWidth: 160,
      sortable: isSortable('contract_number'),
      ...filterOn('text', 'contract_number'),
      renderCell: (value, row) => (
        <span className="text-text-secondary">
          {row.contractNumberMissing
            ? t('tenant.documents.contracts.contractNumberMissing')
            : value
              ? String(value)
              : '—'}
        </span>
      ),
    },
    {
      id: 'contract_status',
      accessor: 'contractStatus',
      header: t('tenant.documents.contracts.columns.contractStatus'),
      defaultVisible: true,
      defaultWidth: 130,
      sortable: isSortable('contract_status'),
      // Lista wyboru z wartościami, które pozwala wybrać karta umowy („Status umowy”), nazwami jak tam
      // (decyzja 2026-10-28); `kanbanFallback: false` — opcje dołożone dla wiersza filtrów nie przejmują
      // domyślnego pola grupującego Kanbana umów.
      ...filterOn('select', 'contract_status', {
        options: codeFilterOptions(CONTRACT_STATUSES, contractStatusLabel, t),
        kanbanFallback: false,
      }),
      renderCell: (value) => (
        <span className="text-text-secondary">
          {value ? contractStatusLabel(String(value), t) : '—'}
        </span>
      ),
    },
    {
      id: 'period',
      accessor: 'periodFrom',
      header: t('tenant.documents.contracts.columns.period'),
      // Slug pola backendu (id ≠ slug) niezależny od filtra — kalendarz listy dopasowuje po nim.
      fieldSlug: 'period_from',
      defaultVisible: true,
      defaultWidth: 200,
      sortable: isSortable('period'),
      // Okres filtruje się po DACIE POCZĄTKU (`period_from`) — ten sam slug co przy sorcie.
      ...filterOn('date', 'period_from'),
      renderCell: (_value, row) => (
        <span className="text-xs text-text-secondary">
          {formatPeriod(
            row.periodFrom ?? null,
            row.periodTo ?? null,
            row.periodIndefinite ?? false,
          )}
        </span>
      ),
    },
    {
      id: 'contract_value',
      accessor: 'contractValue',
      header: t('tenant.documents.contracts.columns.contractValue'),
      defaultVisible: true,
      defaultWidth: 140,
      sortable: isSortable('contract_value'),
      dataType: 'number',
      ...filterOn('number', 'contract_value'),
      renderCell: (_value, row) => (
        <span className="tabular-nums text-text-primary">
          {row.valueConfidential
            ? t('tenant.documents.contracts.valueConfidential')
            : formatAmount(row.contractValue)}
        </span>
      ),
    },
    {
      id: 'counterparty',
      accessor: 'counterpartyNameCached',
      header: t('tenant.documents.contracts.columns.counterparty'),
      // Slug pola backendu (id ≠ slug) niezależny od filtra — kalendarz listy dopasowuje po nim.
      fieldSlug: 'counterparty_name_cached',
      defaultVisible: true,
      defaultWidth: 240,
      sortable: isSortable('counterparty'),
      // Strona główna filtruje się po buforowanej NAZWIE (`counterparty_name_cached`), jak sort.
      ...filterOn('text', 'counterparty_name_cached'),
      renderCell: (_value, row) => (
        <div className="text-text-primary">
          {row.counterpartyNameCached ?? '—'}
          {row.counterpartyTaxIdCached ? (
            <span className="ml-2 text-xs text-text-muted">NIP {row.counterpartyTaxIdCached}</span>
          ) : null}
        </div>
      ),
    },
  ];
}

/**
 * `id`, `gcrecord` (zawsze w `alwaysSlugs`) i `title` (kolumna `hideable: false`) są jedynymi polami
 * GWARANTOWANYMI w każdej odpowiedzi — reszta zależy od tego, co user aktualnie widzi (Kreator
 * Listy może schować każdą inną kolumnę, a backend w trybie projekcji `?columns=` zwraca WYŁĄCZNIE
 * zażądane pola). Typ wiersza listy umów; wiersz sekcji kokpitu decyzji = ten wiersz + kroki decyzji.
 */
export interface ContractRow {
  id: string;
  companyId?: string;
  systemNumber?: string | null;
  title: string | null;
  status?: string;
  urgency?: string;
  description?: string | null;
  notes?: string | null;
  rejectionReason?: string | null;
  ownerUserId?: string | null;
  source?: string;
  externalId?: string | null;
  externalSource?: string | null;
  documentDate?: string | null;
  valueCached?: string | null;
  counterpartyNameCached?: string | null;
  counterpartyTaxIdCached?: string | null;
  contractorId?: string | null;
  /** Nazwa kategorii z `record_categories` (LEFT JOIN w deskryptorze) — kolumna „Kategoria". */
  categoryName?: string | null;
  gcrecord: string | null;
  createdAt?: string;
  updatedAt?: string;
  createdByUserId?: string;
  updatedByUserId?: string;
  // document_contract_details
  contractNumber?: string | null;
  contractNumberMissing?: boolean;
  periodFrom?: string | null;
  periodTo?: string | null;
  periodIndefinite?: boolean;
  periodDurationValue?: number | null;
  periodDurationUnit?: string | null;
  contractValue?: string | null;
  contractValueDescription?: string | null;
  contractStatus?: string | null;
  subjectMatter?: string | null;
  subjectMatterConfidential?: boolean;
  valueConfidential?: boolean;
  cruNumber?: number | null;
  cruPublishedAt?: string | null;
  /**
   * Kolumny przekrojowe silnika list (`apps/api/lib/list-engine/providers/process-columns.ts`)
   * — DOKŁADANE na każdym wierszu gdy tenant ma aktywny moduł `processes`, POMIJANE
   * całkowicie (klucz nieobecny) gdy moduł nieaktywny. `currentStage` to bieżące etapy sprawy
   * w toku (0..N, gałęzie równoległe) albo nazwa etapu końcowego sprawy zakończonej (jeden element).
   */
  activeProcessName?: string | null;
  currentStage?: string[];
  /**
   * Wartości pól dodatkowych (ext) — obecne TYLKO gdy backend odpowiada w trybie
   * projekcji `?columns=` i zażądano ≥1 aktywnego pola ext (kontrakt jak
   * `GET /api/products?columns=` / `GET /api/contractors?columns=`).
   */
  ext?: Record<string, unknown>;
}
